import {useCallback, useEffect, useRef, useState} from 'react';
import type {TDashboardTablePaginationLayout} from '@/pages/datalab/components/CustomComponents/TableAnalytics';

type TMode = TDashboardTablePaginationLayout;

/** 分页条判定为换行（藏每页条数） */
const PAGINATION_WRAP_ENTER_PX = 44;
/** 认为回到单行（可尝试恢复每页条数） */
const PAGINATION_WRAP_EXIT_PX = 36;
/** 相对「藏每页条数」时卡片宽度，再宽出多少才恢复每页条数 */
const RESTORE_PAGE_SIZE_EXTRA_W = 24;
/** 相对进入「仅总条数」时卡片宽度，再宽出多少才恢复翻页区 */
const RESTORE_PAGER_EXTRA_W = 32;

/**
 * 仅观察卡片根节点；在 rAF 内读子节点分页高度，避免在 RO 回调里同步改布局触发循环。
 */
export function useTableCardPaginationLayout(
  cardRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
): TMode {
  const [mode, setMode] = useState<TMode>('full');
  const modeRef = useRef<TMode>('full');
  const widthWhenHidPageSizeRef = useRef<number | null>(null);
  const widthWhenEnterTotalOnlyRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);

  const applyMode = useCallback((next: TMode) => {
    if (modeRef.current === next) return;
    modeRef.current = next;
    setMode(next);
  }, []);

  const measurePaginationHeight = useCallback((root: HTMLElement) => {
    const pag = root.querySelector('.tea-pagination') as HTMLElement | null;
    if (!pag) return 0;
    return Math.ceil(pag.getBoundingClientRect().height);
  }, []);

  const compute = useCallback(() => {
    const root = cardRef.current;
    if (!root || !enabled) return;

    const w = Math.floor(root.clientWidth);
    const pagH = measurePaginationHeight(root);
    const cur = modeRef.current;

    if (cur === 'totalOnly') {
      const baseline = widthWhenEnterTotalOnlyRef.current;
      if (baseline != null && w >= baseline + RESTORE_PAGER_EXTRA_W) {
        widthWhenEnterTotalOnlyRef.current = null;
        widthWhenHidPageSizeRef.current = w;
        applyMode('hidePageSize');
      }
      return;
    }

    if (cur === 'hidePageSize') {
      if (pagH > PAGINATION_WRAP_ENTER_PX) {
        widthWhenEnterTotalOnlyRef.current = w;
        applyMode('totalOnly');
        return;
      }
      if (pagH > 0 && pagH < PAGINATION_WRAP_EXIT_PX) {
        const baseline = widthWhenHidPageSizeRef.current;
        if (baseline != null && w >= baseline + RESTORE_PAGE_SIZE_EXTRA_W) {
          widthWhenHidPageSizeRef.current = null;
          applyMode('full');
        }
      }
      return;
    }

    // full
    if (pagH > PAGINATION_WRAP_ENTER_PX) {
      widthWhenHidPageSizeRef.current = w;
      applyMode('hidePageSize');
    }
  }, [applyMode, cardRef, enabled, measurePaginationHeight]);

  const schedule = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      compute();
    });
  }, [compute]);

  useEffect(() => {
    if (!enabled) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      modeRef.current = 'full';
      widthWhenHidPageSizeRef.current = null;
      widthWhenEnterTotalOnlyRef.current = null;
      setMode('full');
      return;
    }

    const root = cardRef.current;
    if (!root || typeof ResizeObserver === 'undefined') {
      schedule();
      return;
    }

    const ro = new ResizeObserver(() => {
      schedule();
    });
    ro.observe(root);
    schedule();

    return () => {
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cardRef, enabled, schedule]);

  return mode;
}
